/**
 * Invoice formatting utilities
 * Utilities for formatting invoice line items, service names, weekdays, and quantities
 */

import type { I18n } from '@lingui/core';
import type { Service, AgeType, InvoiceLineItem } from '@/types/billing';
import { DEFAULT_CURRENCY, formatMoney, minorToMajor } from '@/lib/money';
import { formatDecimal, formatNumber } from '@/lib/format';

/**
 * Lingui message descriptors for the child-extra type labels (synthetic
 * "EXTRA_<TYPE>" service codes emitted by child_extras line items).
 */
const EXTRA_TYPE_LABELS: Record<string, { id: string; message: string }> = {
  EXTRA_EINSCH: { id: 'extras.type.einsch', message: 'Registration fee' },
  EXTRA_EMPFEHL: { id: 'extras.type.empfehl', message: 'Referral bonus' },
  EXTRA_MISC: { id: 'extras.type.misc', message: 'Miscellaneous' },
  EXTRA_LEGACY: { id: 'extras.type.legacy', message: 'Legacy adjustment' },
  EXTRA_SETTLING_IN: { id: 'extras.type.settlingIn', message: 'Settling-in' },
  EXTRA_TERMINATION: { id: 'extras.type.termination', message: 'Exit fee' },
};

/**
 * Get localized service name from billing settings
 * Looks up the service by code and returns the name in the specified locale
 * Falls back to name_en or the base name field if locale not available
 */
export function getLocalizedServiceName(
  serviceCode: string,
  services: Service[],
  locale: string,
  i18n?: I18n
): string {
  // Handle settling-in service code with i18n
  if (serviceCode === 'SETTLING_IN' && i18n) {
    return i18n._({ id: 'settlingIn.title', message: 'Settling-In' });
  }

  // Child-extra line items carry a synthetic "EXTRA_<TYPE>" service code that
  // is not present in BillingSettings.Services. Resolve via the dedicated
  // Lingui keys shared with the debtor extras tab.
  if (i18n && serviceCode.startsWith('EXTRA_')) {
    const descriptor = EXTRA_TYPE_LABELS[serviceCode];
    if (descriptor) {
      return i18n._(descriptor);
    }
  }

  const service = services.find((s) => s.code === serviceCode);
  if (!service) {
    return serviceCode; // Fallback to code if service not found
  }

  // Map locale to service name field
  const localeFieldMap: Record<string, keyof Service> = {
    de: 'name_de',
    en: 'name_en',
    es: 'name_es',
    fr: 'name_fr',
    it: 'name_it',
    pt: 'name_pt',
  };

  const field = localeFieldMap[locale];
  const localizedName = field ? service[field] : undefined;

  // Fallback chain: requested locale -> name_en -> name -> code
  return (
    (localizedName as string | undefined) ||
    service.name_en ||
    service.name ||
    serviceCode
  );
}

/**
 * Get localized weekday name from integer (1=Monday, 7=Sunday)
 * Uses Lingui i18n for proper localization
 */
export function getWeekdayName(weekday: number, i18n: I18n): string {
  const weekdayMap: Record<number, { id: string; message: string }> = {
    1: { id: 'common.days.monday', message: 'Monday' },
    2: { id: 'common.days.tuesday', message: 'Tuesday' },
    3: { id: 'common.days.wednesday', message: 'Wednesday' },
    4: { id: 'common.days.thursday', message: 'Thursday' },
    5: { id: 'common.days.friday', message: 'Friday' },
    6: { id: 'common.days.saturday', message: 'Saturday' },
    7: { id: 'common.days.sunday', message: 'Sunday' },
  };

  const day = weekdayMap[weekday];
  if (!day) return '-';
  return i18n._(day);
}

/**
 * Get short localized weekday name from integer (1=Monday, 7=Sunday)
 * Returns abbreviated form (Mon, Tue, etc.)
 */
export function getWeekdayNameShort(weekday: number, i18n: I18n): string {
  const weekdayMap: Record<number, { id: string; message: string }> = {
    1: { id: 'common.days.short.monday', message: 'Mon' },
    2: { id: 'common.days.short.tuesday', message: 'Tue' },
    3: { id: 'common.days.short.wednesday', message: 'Wed' },
    4: { id: 'common.days.short.thursday', message: 'Thu' },
    5: { id: 'common.days.short.friday', message: 'Fri' },
    6: { id: 'common.days.short.saturday', message: 'Sat' },
    7: { id: 'common.days.short.sunday', message: 'Sun' },
  };

  const day = weekdayMap[weekday];
  if (!day) return '-';
  return i18n._(day);
}

/**
 * Format quantity display for invoice line item with i18n support
 * Formats quantity based on structured fields from backend (weekFactor, periodDays, monthDays)
 * Supports pro-rata temporis display and week-based calculations
 * Note: Fields are in camelCase after InvoiceTransformer.toFrontend() transformation
 */
export function formatQuantity(lineItem: InvoiceLineItem, i18n: I18n): string {
  // Handle both camelCase (transformed) and snake_case (raw API) field names
  const itemData = lineItem as Record<string, unknown>;
  const quantity = (itemData.quantity as number) ?? 0;
  const periodDays = (itemData.periodDays as number | undefined) ?? (itemData.period_days as number | undefined);
  const monthDays = (itemData.monthDays as number | undefined) ?? (itemData.month_days as number | undefined);
  const weekFactor = (itemData.weekFactor as number | undefined) ?? (itemData.week_factor as number | undefined);
  const weekFactorWhole = (itemData.weekFactorWhole as boolean | undefined) ?? (itemData.week_factor_whole as boolean | undefined);
  const locale = i18n.locale;

  // Week factor method: show as "X weeks" or pro-rata format
  if (weekFactor !== undefined) {
    // A factor of exactly 1 reads "1 week" even when the backend flags it fractional.
    const whole = weekFactorWhole || weekFactor === 1;
    const weekCount = whole ? Math.round(weekFactor) : Number(weekFactor.toFixed(1));
    const weeks = whole ? formatNumber(weekCount, locale) : formatDecimal(weekFactor, locale, 1);

    // Pro-rata temporis (partial month): "18/31 days (4.2 wk)"
    if (periodDays !== undefined && monthDays !== undefined) {
      return i18n._(
        { id: 'invoice.quantity.proRata', message: '{periodDays}/{monthDays} days ({weeks} wk)' },
        { periodDays: formatNumber(periodDays, locale), monthDays: formatNumber(monthDays, locale), weeks }
      );
    }
    // The plural takes the number, the text shows it formatted for the locale.
    return i18n._(
      {
        id: 'invoice.quantity.weeksValue',
        message: '{count, plural, one {{weeks} week} other {{weeks} weeks}}',
      },
      { count: weekCount, weeks }
    );
  }

  // Actual days method: show as "X days"
  return i18n._(
    {
      id: 'invoice.quantity.daysValue',
      message: '{count, plural, one {{days} day} other {{days} days}}',
    },
    { count: quantity, days: formatNumber(quantity, locale) }
  );
}

/**
 * Format an invoice amount stored in minor units (Rappen/cents).
 */
export function formatCurrency(
  amountInRappen: number,
  includeSymbol: boolean = true,
  currency: string = DEFAULT_CURRENCY
): string {
  return formatMoney(minorToMajor(amountInRappen), includeSymbol, currency);
}

/**
 * Format age type for display
 * Converts BABY/CHILD to localized string
 */
export function formatAgeType(
  ageType: AgeType | string | undefined,
  i18n: I18n
): string {
  if (!ageType) return '';

  const ageTypeMap: Record<
    string,
    { id: string; message: string }
  > = {
    BABY: { id: 'tariff.ageTypes.baby', message: 'Baby' },
    CHILD: { id: 'tariff.ageTypes.child', message: 'Child' },
    SETTLING_IN: { id: 'settlingIn.title', message: 'Settling-In' },
    // Child-extras don't carry an age_type, so the aggregation hook buckets
    // them under "UNKNOWN"; surface that as the Extras section heading.
    UNKNOWN: { id: 'invoice.breakdown.extrasSection', message: 'Extras' },
  };

  const type = ageTypeMap[ageType];
  return type ? i18n._(type) : ageType;
}
